import { useState, useMemo, useEffect } from 'react';
import { useGetOrganizationsQuery } from '@/entities/organizations';
import { Contact, useGetOrganizationContactsQuery } from '@/entities/contacts';
import { useProjectSiteParams } from '@/shared/lib';

interface UseContactSelectorProps {
  organizationId?: string;
  organizationName?: string;
  contactId?: string;
  contactName?: string;
  onOrganizationChange: (id: string | undefined) => void;
  onContactChange: (id: string | undefined) => void;
}

export const useContactSelector = ({
  organizationId,
  organizationName,
  contactId,
  contactName,
  onOrganizationChange,
  onContactChange
}: UseContactSelectorProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const { projectId, siteId } = useProjectSiteParams();

  const { data: orgData, isLoading: isLoadingOrgs } = useGetOrganizationsQuery({
    projectId: projectId as number
  });

  useEffect(() => {
    if (orgData?.organizations && !organizationId && organizationName) {
      const match = orgData.organizations.find((org) => org.name.toLowerCase() === organizationName.toLowerCase());
      if (match) {
        onOrganizationChange(String(match.id));
      }
    }
  }, [orgData, organizationId, organizationName, onOrganizationChange]);

  const { data: contactData, isLoading: isLoadingContacts } = useGetOrganizationContactsQuery(
    {
      orgId: organizationId as string,
      projectId: projectId as number,
      siteId: siteId as number
    },
    { skip: !organizationId || !projectId || !siteId }
  );

  const orgOptions = useMemo(() => {
    const options = orgData?.organizations.map((org) => ({ value: String(org.id), label: org.name })) || [];
    if (organizationId && organizationName && !options.find((o) => o.value === organizationId)) {
      options.push({ value: organizationId, label: organizationName });
    }
    return options;
  }, [orgData, organizationId, organizationName]);

  const contactOptions = useMemo(() => {
    const options = contactData?.map((c) => ({ value: String(c.id), label: c.name })) || [];
    if (contactId && contactName && !options.find((o) => o.value === contactId)) {
      options.push({ value: contactId, label: contactName });
    }
    return options;
  }, [contactData, contactId, contactName]);

  const selectedContact = contactData?.find((c) => String(c.id) === contactId);

  const handleAddClick = () => {
    setIsEditing(false);
    setIsModalOpen(true);
  };

  const handleEditClick = () => {
    setIsEditing(true);
    setIsModalOpen(true);
  };

  const handleModalClose = () => setIsModalOpen(false);

  const handleModalSuccess = (contact: Contact) => {
    if (contact.orgId) {
      onOrganizationChange(String(contact.orgId));
    }
    onContactChange(String(contact.id));
    setIsModalOpen(false);
  };

  const toggleDetails = () => setShowDetails((prev) => !prev);

  return {
    isModalOpen,
    isEditing,
    showDetails,
    isLoadingOrgs,
    isLoadingContacts,
    orgOptions,
    contactOptions,
    selectedContact,
    handleAddClick,
    handleEditClick,
    handleModalClose,
    handleModalSuccess,
    toggleDetails
  };
};
